import type { Show, ShowSummary, ShowWithGenre, ShowListItem } from '@musedrops/shared';
import {
  fromDbKeys,
  toDbKeys,
  snakeCase,
  logger,
  withUpdateTimestamp,
  withCreateTimestamp,
} from '@musedrops/shared';
import { supabase } from '../services/supabase';
import { isUuid, orIlikeContains } from '../utils/postgrest';

// Lifecycle processing is handled automatically by the on-data-change Edge Function
// which receives database triggers on INSERT/UPDATE/DELETE.

export interface GetAllShowsOptions {
  page?: number;
  perPage?: number;
  sortField?: keyof Show | string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

export async function getAllShows(
  options: GetAllShowsOptions = {}
): Promise<{ data: ShowWithGenre[]; total?: number }> {
  const {
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter: originalFilter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  // Join show_translations to get description and audioLogoDesc
  // These fields live in show_translations table
  // We get the translation for the show's primary language (or 'en' fallback)
  let query = supabase
    .from('shows')
    .select(
      `*,
      genre:genres!shows_genre_id_fkey(*),
      translations:show_translations!left(description, audio_logo_desc, lang)`,
      selectOptions
    );

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  // Extract search query before toDbKeys conversion
  let searchQuery: string | undefined;
  let filter = originalFilter;

  if (filter?.q && typeof filter.q === 'string') {
    searchQuery = filter.q;
    // Remove q from filter object before toDbKeys
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { q: _q, ...restFilter } = filter as {
      q: string;
      [key: string]: unknown;
    };
    filter = restFilter;
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  // Apply text search across topic and taxonomy
  // name is in show_translations, so only search topic and taxonomy on shows table
  if (searchQuery) {
    query = query.or(
      `topic.ilike.%${searchQuery}%,taxonomy.ilike.%${searchQuery}%`
    );
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
    });
  } else {
    query = query.order('updated_at', { ascending: false });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch shows: ${error.message}`);
  }

  // Extract description and audioLogoDesc from translations
  // Find the translation matching the show's lang, or fallback to 'en', or first available
  return {
    data: (data ?? []).map(row => {
      const rawRow = row as Record<string, unknown>;
      const translations = rawRow.translations as Array<{
        description: string | null;
        audio_logo_desc: string | null;
        lang: string;
      }> | null;

      // Find best matching translation
      const showLang = rawRow.lang as string | null;
      let translation = translations?.find(t => t.lang === (showLang || 'en'));
      if (!translation && translations?.length) {
        [translation] = translations;
      }

      // Add description and audioLogoDesc to row before conversion
      const enrichedRow = {
        ...rawRow,
        description: translation?.description ?? null,
        audio_logo_desc: translation?.audio_logo_desc ?? null,
      };
      delete enrichedRow.translations; // Remove translations array

      return fromDbKeys<ShowWithGenre>(enrichedRow);
    }),
    total: withTotal ? (count ?? 0) : undefined,
  };
}

export interface ListShowsWithTransOptions {
  page?: number;
  perPage?: number;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

/**
 * List shows with aggregated translations for ShowList display.
 * Uses shows_with_trans view for language column display.
 */
export async function listShowsWithTrans(
  options: ListShowsWithTransOptions = {}
): Promise<{ data: ShowListItem[]; total?: number }> {
  const {
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter: originalFilter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  let query = supabase
    .from('shows_with_trans')
    .select('*', selectOptions);

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  // Extract search query before toDbKeys conversion
  let searchQuery: string | undefined;
  let filter = originalFilter;

  if (filter?.q && typeof filter.q === 'string') {
    searchQuery = filter.q;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { q: _q, ...restFilter } = filter as {
      q: string;
      [key: string]: unknown;
    };
    filter = restFilter;
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  // Apply text search across topic (name is in show_translations)
  if (searchQuery) {
    query = query.or(orIlikeContains(['topic'], searchQuery));
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
      // Keep shows without a value (e.g. null last_story_at) at the bottom
      // regardless of sort direction.
      nullsFirst: false,
    });
  } else {
    query = query.order('updated_at', { ascending: false });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch shows with translations: ${error.message}`);
  }

  return {
    data: (data ?? []).map(row =>
      fromDbKeys<ShowListItem>(row as Record<string, unknown>)
    ),
    total: withTotal ? (count ?? 0) : undefined,
  };
}

// Issue #787: how far back to look for a show's last published story translation.
// Bounds the row scan on stories_admin_enriched (PostgREST default row cap is 1000).
// Anything older than this is treated as "no recent publish" regardless of exact age -
// no realistic publish schedule (weekly or more frequent) tolerates a gap this long, so
// the precise age beyond the window doesn't change the schedule-violation verdict.
export const LAST_PUBLISHED_LOOKBACK_DAYS = 60;
const LAST_PUBLISHED_ROW_CAP = 2000; // headroom above the ~1000-row PostgREST default cap

export interface ShowPublishStatus {
  // ISO timestamp of the most recent PUBLISHED story translation for this show,
  // within the lookback window. Null if none found (show may still have older
  // publishes, or none at all - see LAST_PUBLISHED_LOOKBACK_DAYS).
  lastPublishedAt: string | null;
  // Show-specific weekday override (raw DB value), for schedule-violation checks.
  customPublishWeekdays: string | null;
}

/**
 * Batched fetch of last-published-translation + schedule-override data for a set
 * of shows, for the ShowList "Last Published" column (issue #787).
 *
 * Deliberately ONE query per concern (not per-row) to avoid N+1s in the list:
 * - stories_admin_enriched: last published translation per show, capped/windowed
 * - shows: custom_publish_weekdays override, batched via .in('slug', ...)
 *
 * Reduction to "latest per show" happens client-side (stories_admin_enriched has
 * no aggregation - it's a per-translation view - so this is not a DB object change).
 */
export async function getShowPublishStatus(
  showSlugs: string[]
): Promise<Record<string, ShowPublishStatus>> {
  const result: Record<string, ShowPublishStatus> = {};
  for (const slug of showSlugs) {
    result[slug] = { lastPublishedAt: null, customPublishWeekdays: null };
  }
  if (showSlugs.length === 0) return result;

  const lookbackDate = new Date();
  lookbackDate.setUTCDate(lookbackDate.getUTCDate() - LAST_PUBLISHED_LOOKBACK_DAYS);

  const [publishedResult, weekdaysResult] = await Promise.all([
    supabase
      .from('stories_admin_enriched')
      .select('show_slug, published_at')
      .eq('phase', 'PUBLISHED')
      .not('published_at', 'is', null)
      .gte('published_at', lookbackDate.toISOString())
      .in('show_slug', showSlugs)
      .order('published_at', { ascending: false })
      .limit(LAST_PUBLISHED_ROW_CAP),
    supabase
      .from('shows')
      .select('slug, custom_publish_weekdays')
      .in('slug', showSlugs),
  ]);

  if (publishedResult.error) {
    throw new Error(
      `Failed to fetch last-published status for shows: ${publishedResult.error.message}`
    );
  }
  if (weekdaysResult.error) {
    throw new Error(
      `Failed to fetch publish schedule overrides for shows: ${weekdaysResult.error.message}`
    );
  }

  // Rows are ordered newest-first, so the first row seen per show_slug is its max.
  const seen = new Set<string>();
  for (const row of publishedResult.data ?? []) {
    const slug = row.show_slug as string;
    if (!seen.has(slug)) {
      seen.add(slug);
      if (!result[slug]) result[slug] = { lastPublishedAt: null, customPublishWeekdays: null };
      result[slug].lastPublishedAt = row.published_at as string;
    }
  }

  for (const row of weekdaysResult.data ?? []) {
    const slug = row.slug as string;
    if (!result[slug]) result[slug] = { lastPublishedAt: null, customPublishWeekdays: null };
    result[slug].customPublishWeekdays = row.custom_publish_weekdays as string | null;
  }

  return result;
}

export async function getShowsByGenre(genreId: string): Promise<ShowSummary[]> {
  // name is in show_translations, join to get it
  const { data, error } = await supabase
    .from('shows')
    .select(`
      id,
      slug,
      translations:show_translations!left(name, lang)
    `)
    .eq('genre_id', genreId)
    .order('slug', { ascending: true });

  if (error) {
    throw new Error(
      `Failed to fetch shows for genre ${genreId}: ${error.message}`
    );
  }

  return (data ?? []).map(row => {
    const rawRow = row as Record<string, unknown>;
    const translations = rawRow.translations as Array<{
      name: string | null;
      lang: string;
    }> | null;

    // Get name from first available translation (prefer 'en')
    const translation = translations?.find(t => t.lang === 'en') ?? translations?.[0];

    return fromDbKeys<ShowSummary>({
      id: rawRow.id,
      slug: rawRow.slug,
      name: translation?.name ?? rawRow.slug, // Fallback to slug if no name
    });
  });
}

export async function updateShow(
  show: Partial<Show> & Pick<Show, 'id'>
): Promise<void> {
  const updatePayload = withUpdateTimestamp(toDbKeys(show));
  logger.info('DEBUG updateShow: payload lang value', { showLang: show.lang, payloadLang: updatePayload.lang });

  const { error } = await supabase
    .from('shows')
    .update(updatePayload)
    .eq('id', show.id);

  if (error) {
    throw new Error(`Failed to update show ${show.id}: ${error.message}`);
  }

  // Note: Lifecycle processing is now handled automatically by on-data-change Edge Function
}

export async function createShow(show: Partial<Show>): Promise<Show> {
  const { data, error } = await supabase
    .from('shows')
    .insert(withCreateTimestamp(toDbKeys(show as Record<string, unknown>)))
    .select('*')
    .single();

  if (error) {
    logger.error('Failed to insert show', {
      error: error.message,
      code: error.code,
    });
    throw new Error(`Failed to insert show: ${error.message}`);
  }

  const createdShow = fromDbKeys<Show>(data);

  // Note: Lifecycle processing is now handled automatically by on-data-change Edge Function

  return createdShow;
}

export async function getShow(id: string): Promise<Show> {
  const { data, error } = await supabase
    .from('shows')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch show ${id}: ${error.message}`);
  }

  return fromDbKeys<Show>(data);
}

export async function getShowWithGenre(slug: string): Promise<ShowWithGenre> {
  const { data, error } = await supabase
    .from('shows')
    .select('*, genre:genres!shows_genre_id_fkey(*)')
    .eq('slug', slug)
    .single();

  if (error) {
    throw new Error(
      `Failed to fetch show-with-genre ${slug}: ${error.message}`
    );
  }

  return fromDbKeys<ShowWithGenre>(data);
}

/**
 * Accepts either a UUID id or a slug.
 */
export async function getShowWithGenreById(idOrSlug: string): Promise<ShowWithGenre> {
  const { data, error } = await supabase
    .from('shows')
    .select('*, genre:genres!shows_genre_id_fkey(*)')
    // Branch on UUID shape: `id` is a uuid column, so filtering it with a slug
    // value would make PostgREST reject the whole query (22P02).
    .eq(isUuid(idOrSlug) ? 'id' : 'slug', idOrSlug)
    .single();

  if (error) {
    throw new Error(`Failed to fetch show-with-genre ${idOrSlug}: ${error.message}`);
  }

  const result = fromDbKeys<ShowWithGenre>(data);
  logger.info('DEBUG getShowWithGenreById: fetched lang value', { rawLang: data.lang, convertedLang: result.lang });
  return result;
}

export async function getShowTopics(genreId?: string): Promise<string[]> {
  let query = supabase
    .from('shows')
    .select('topic')
    .eq('enabled', true)
    .order('updated_at', { ascending: false });

  if (genreId) {
    query = query.eq('genre_id', genreId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch show topics: ${error.message}`);
  }

  return (data ?? [])
    .map(
      row =>
        fromDbKeys<{ topic?: string }>(row as Record<string, unknown>).topic
    )
    .filter((topic): topic is string => !!topic);
}

export async function checkSlugUniqueness(slug: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('shows')
    .select('slug')
    .eq('slug', slug)
    .limit(1);

  return !error && data?.length === 0;
}

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
export async function updateShowImage(
  slug: string,
  imageFile: string
): Promise<void> {
  const { getTimestampField } = await import('@musedrops/shared');

  if (imageFile) {
    // Parameter retained for API compatibility; no-op by design.
  }

  // Update only the timestamp field (Phase 4: image_file column removed)
  const timestampField = getTimestampField('show', 'coverImage');

  const { error } = await supabase
    .from('shows')
    .update(
      withUpdateTimestamp({
        [timestampField]: new Date().toISOString(),
      })
    )
    .eq('slug', slug);

  if (error) {
    throw new Error(`Failed to update show image: ${error.message}`);
  }
}

/**
 * Count stories for a show and update the story_count field
 * More reliable than increment/decrement since it always reflects the true count
 */
export async function updateShowStoryCount(showSlug: string): Promise<void> {
  // Count stories for this show
  const { count, error: countError } = await supabase
    .from('stories')
    .select('id', { count: 'exact', head: true })
    .eq('show_slug', showSlug);

  if (countError) {
    throw new Error(
      `Failed to count stories for show ${showSlug}: ${countError.message}`
    );
  }

  // Update the show with the count
  const { error: updateError } = await supabase
    .from('shows')
    .update({ story_count: count ?? 0 })
    .eq('slug', showSlug);

  if (updateError) {
    throw new Error(
      `Failed to update story_count for show ${showSlug}: ${updateError.message}`
    );
  }
}
